/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionPanel,
  Grid,
  ShowMoreList,
  Typography,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { ModuleLayoutConfig } from 'bundles/unified-common/modules/ModuleLayout';
import ModuleLayout, { getModuleLayoutConfig, noImage } from 'bundles/unified-common/modules/ModuleLayout';

import _t from 'i18n!nls/xdp';

export type FaqModuleItem = {
  question?: string;
  answer?: string | React.ReactNode;
};

type Props = {
  faqs?: FaqModuleItem[] | null;
  moreInfo?: React.ReactNode;
  accordionTrackingData?: Omit<EventData<'expand_accordion'>, 'accordion'>;
} & Omit<ModuleLayoutConfig, 'preloadImage' | 'lazyLoadImage'>;

const MAX_FAQS_TO_SHOW = 3;

const styles = {
  faqContentWrapper: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400) var(--cds-spacing-150);
    }

    button:focus {
      outline: none;
    }
  `,
  moreInfoContentWrapper: css`
    align-self: flex-start;
    width: 100%;
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-150);
      margin-top: var(--cds-spacing-100);
    }
  `,
  titleContainer: css`
    margin-bottom: calc(var(--cds-spacing-100) * -1);
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  faqContentContainer: css`
    margin-top: var(--cds-spacing-100);
  `,
  accordionGroup: css`
    border-width: 2px;
  `,
  accordionItemContainer: css`
    border-bottom: 1px solid var(--cds-color-grey-50);
    padding-bottom: var(--cds-spacing-100);
  `,
  showMoreList: css`
    .cds-ShowMoreList-listContainer {
      padding: var(--cds-spacing-50);
    }
  `,
};

const FAQsModule = (props: Props) => {
  const { faqs, moreInfo, accordionTrackingData } = props;
  const tracker = useTracker();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const filteredFaqs = faqs?.filter((faq) => !!faq?.question && !!faq?.answer);

  if (!filteredFaqs?.length) {
    return null;
  }

  const moduleLayoutConfig: ModuleLayoutConfig = getModuleLayoutConfig(props);

  const renderContent = () => (
    <Grid container spacing={isMobile ? 16 : 32}>
      <Grid item xs={12} css={styles.titleContainer}>
        <Typography variant="h1semibold" component="h2">
          {_t('Frequently asked questions')}
        </Typography>
      </Grid>
      <Grid item md={moreInfo ? 8 : 12} xs={12}>
        <div css={styles.faqContentWrapper}>
          <AccordionGroup spacing={8}>
            <ShowMoreList
              numItemsToShow={MAX_FAQS_TO_SHOW}
              controllerProps={{ itemTypePlural: _t('frequently asked questions') }}
              css={styles.showMoreList}
            >
              {filteredFaqs.map((faq, index) => {
                if (!faq.question) {
                  return <div key={faq.question} />;
                } else {
                  const onChange = (event: React.ChangeEvent<unknown>, expanded: boolean) => {
                    tracker(expanded ? 'expand_accordion' : 'collapse_accordion', {
                      accordion: {
                        name: 'faq',
                        header: faq.question,
                      },
                      ...accordionTrackingData,
                    });
                  };
                  return (
                    <TrackedDiv
                      trackingName="faq_accordion"
                      css={index !== filteredFaqs.length - 1 ? styles.accordionItemContainer : {}}
                      key={faq.question}
                    >
                      <Accordion variant="silent" onChange={onChange}>
                        <AccordionHeader label={faq.question} />
                        <AccordionPanel>
                          <div css={styles.faqContentContainer}>{faq.answer}</div>
                        </AccordionPanel>
                      </Accordion>
                    </TrackedDiv>
                  );
                }
              })}
            </ShowMoreList>
          </AccordionGroup>
        </div>
      </Grid>
      {moreInfo && (
        <Grid item md={4} xs={12}>
          <div css={styles.moreInfoContentWrapper}>{moreInfo}</div>
        </Grid>
      )}
    </Grid>
  );

  return <ModuleLayout renderContent={renderContent} assembly={noImage(moduleLayoutConfig)} />;
};

export default FAQsModule;
